import { ImageIcon, Inbox, Loader2, X } from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStore } from '@nanostores/react';

import { PATIENT_CHAT_HISTORY_ANCHOR } from '@/app/hints';
import {
  flattenPrimaryChatPages,
  hasAttachmentContentBlock,
  usePrimaryChatInfiniteQuery,
} from '@/entities/user-chat';
import { cn } from '@/lib/utils';
import { useUserInfoQuery } from '@/entities/user-info';
import { toDayKeyYYYYMMDD } from '@/shared/lib';
import { EmptyState, useHintAnchor } from '@/shared/ui/primitives';
import { $patientChatFocusTarget, clearPatientChatFocusTarget } from '@/shared/stores';
import { Button } from '@/shared/ui/shadcn/button';

import { ChatDayDivider } from './ChatDayDivider';
import { ChatLoadingSkeleton } from './ChatLoadingSkeleton';
import { ChatMessageRow } from './ChatMessageRow';
import { isNearBottom, shouldFollowNewMessage } from '../model/followNewMessages';

const LOAD_OLDER_SCROLL_THRESHOLD_PX = 120;

export function PatientChatTab({ userId }: { userId: string }) {
  const chatQuery = usePrimaryChatInfiniteQuery(userId);
  const userInfoQuery = useUserInfoQuery(userId);
  const flattened = useMemo(
    () => flattenPrimaryChatPages(chatQuery.data?.pages),
    [chatQuery.data?.pages]
  );
  const messages = flattened.messages;
  const conversationId = flattened.conversationId;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hintAnchorRef = useHintAnchor(PATIENT_CHAT_HISTORY_ANCHOR);
  // Scroll container needs both the imperative ref (scroll math) and the hint callback.
  const scrollAndHintRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollRef.current = el;
      return hintAnchorRef(el);
    },
    [hintAnchorRef]
  );
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const didAutoScrollRef = useRef<string | null>(null);
  // Whether the reader was at the bottom as of their last scroll, and the
  // newest message last rendered: together they decide whether a message that
  // arrives on a refetch scrolls into view.
  const wasNearBottomRef = useRef(true);
  const previousNewestIdRef = useRef<string | null>(null);
  const messageElByIdRef = useRef<Map<string, HTMLDivElement>>(new Map());
  // Saved metrics taken right before the next page is fetched, used to keep
  // the user's viewport stable when older messages are prepended.
  const scrollAnchorRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [showAttachmentsOnly, setShowAttachmentsOnly] = useState(false);
  const focusTarget = useStore($patientChatFocusTarget);

  const patientNameValue = userInfoQuery.data?.patientInfo?.name;
  const patientName =
    typeof patientNameValue === 'string' && patientNameValue.trim() !== ''
      ? patientNameValue.trim()
      : 'Patient';

  const visibleMessages = useMemo(() => {
    if (!showAttachmentsOnly) {
      return messages;
    }
    return messages.filter((message) => hasAttachmentContentBlock(message));
  }, [messages, showAttachmentsOnly]);

  const dayKeyByMessageId = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const m of visibleMessages) {
      map.set(m.id, toDayKeyYYYYMMDD(m.createdAt));
    }
    return map;
  }, [visibleMessages]);

  const hasNextPage = chatQuery.hasNextPage;
  const isFetchingNextPage = chatQuery.isFetchingNextPage;
  const fetchNextPage = chatQuery.fetchNextPage;

  // Capture scroll metrics before the next page request so we can restore the
  // viewport after older messages are prepended.
  const requestOlderPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    const el = scrollRef.current;
    if (el) {
      scrollAnchorRef.current = {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
      };
    }
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    wasNearBottomRef.current = isNearBottom(el);
    if (el.scrollTop <= LOAD_OLDER_SCROLL_THRESHOLD_PX) {
      requestOlderPage();
    }
  }, [requestOlderPage]);

  // Restore scroll position after older messages are prepended. Runs before
  // paint so the user never sees the pre-restoration frame.
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor) {
      return;
    }
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const delta = el.scrollHeight - anchor.scrollHeight;
    if (delta > 0) {
      el.scrollTop = anchor.scrollTop + delta;
    }
  }, [messages.length]);

  // Follow new messages (a patient reply, a Lotus follow-up) as a refetch brings
  // them in, but only for a reader already at the bottom: someone reading older
  // history keeps their place. Prepending older pages leaves the newest
  // message unchanged, so it never triggers this.
  const newestMessageId = messages.at(-1)?.id ?? null;
  useLayoutEffect(() => {
    const previousNewestId = previousNewestIdRef.current;
    previousNewestIdRef.current = newestMessageId;
    if (
      shouldFollowNewMessage({
        previousNewestId,
        newestId: newestMessageId,
        wasNearBottom: wasNearBottomRef.current,
      })
    ) {
      bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, [newestMessageId]);

  // Always discard the anchor once the in-flight prepend settles, even if the
  // page came back empty (delta === 0) or messages.length didn't change. This
  // prevents stale metrics from leaking into a later, unrelated re-render
  // (e.g. a CareStateQuerySync invalidation appending a new bottom message).
  useEffect(() => {
    if (!isFetchingNextPage) {
      scrollAnchorRef.current = null;
    }
  }, [isFetchingNextPage]);

  // Auto-paginate while there is an outstanding focus target for this
  // conversation that is not yet loaded.
  useEffect(() => {
    if (!focusTarget || focusTarget.userId !== userId) {
      return;
    }
    if (!conversationId || focusTarget.conversationId !== conversationId) {
      return;
    }
    if (messages.some((m) => m.id === focusTarget.messageId)) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    requestOlderPage();
  }, [
    focusTarget,
    userId,
    conversationId,
    messages,
    hasNextPage,
    isFetchingNextPage,
    requestOlderPage,
  ]);

  useLayoutEffect(() => {
    if (!chatQuery.isSuccess || visibleMessages.length === 0) {
      return;
    }
    if (!conversationId) {
      return;
    }

    if (focusTarget?.userId === userId && focusTarget?.conversationId === conversationId) {
      const el = messageElByIdRef.current.get(focusTarget.messageId);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        clearPatientChatFocusTarget();
        window.setTimeout(() => {
          setHighlightedMessageId(focusTarget.messageId);
          window.setTimeout(() => setHighlightedMessageId(null), 2500);
        }, 0);
        return;
      }
      const existsInConversation = messages.some((message) => message.id === focusTarget.messageId);
      if (existsInConversation) {
        return;
      }
      // If the message is not loaded but more pages remain, the auto-paginate
      // effect above will fetch them; bail out here without clearing.
      if (hasNextPage) {
        return;
      }
      clearPatientChatFocusTarget();
    }

    if (didAutoScrollRef.current !== conversationId) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
      didAutoScrollRef.current = conversationId;
    }
  }, [
    conversationId,
    chatQuery.isSuccess,
    focusTarget,
    hasNextPage,
    messages,
    userId,
    visibleMessages.length,
  ]);

  if (chatQuery.isLoading) {
    return <ChatLoadingSkeleton />;
  }

  if (chatQuery.isError) {
    return (
      <EmptyState
        icon={<Inbox className="h-10 w-10" />}
        title="Could not load chat"
        description="Primary conversation could not be fetched."
      />
    );
  }

  if (messages.length === 0) {
    return (
      <EmptyState
        icon={<Inbox className="h-10 w-10" />}
        title="No chat yet"
        description="This patient has no primary conversation messages."
      />
    );
  }

  return (
    <div
      ref={scrollAndHintRef}
      onScroll={handleScroll}
      className="h-full overflow-auto"
      data-testid="patient-chat-scroll"
    >
      <div className="mx-auto w-full max-w-2xl space-y-3">
        <div className="sticky top-2 z-10 flex flex-col items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant={showAttachmentsOnly ? 'secondary' : 'outline'}
            aria-pressed={showAttachmentsOnly}
            onClick={() => setShowAttachmentsOnly((prev) => !prev)}
            className={cn(
              'h-8 rounded-full gap-1.5 px-3 text-xs shadow-sm backdrop-blur-sm border',
              showAttachmentsOnly
                ? 'bg-secondary border-primary text-foreground'
                : 'bg-background/90 border-border'
            )}
          >
            <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Show attachments only
            {showAttachmentsOnly ? <X className="h-3.5 w-3.5" aria-hidden="true" /> : null}
          </Button>
          {showAttachmentsOnly ? (
            <span className="text-[10px] text-muted-foreground">
              Showing loaded messages — scroll up to load more.
            </span>
          ) : null}
        </div>

        <div
          className="flex justify-center py-2 text-xs text-muted-foreground"
          aria-live="polite"
          data-testid="patient-chat-top-status"
        >
          {isFetchingNextPage ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Loading older messages…
            </span>
          ) : hasNextPage ? (
            <button
              type="button"
              className="rounded-full border bg-background/90 px-3 py-1 shadow-sm hover:bg-secondary"
              onClick={requestOlderPage}
            >
              Load older messages
            </button>
          ) : (
            <span>Beginning of conversation</span>
          )}
        </div>

        {visibleMessages.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-10 w-10" />}
            title="No attachment messages"
            description="No chat messages with file attachments were found."
          />
        ) : null}

        {visibleMessages.map((m, index) => {
          const dayKey = dayKeyByMessageId.get(m.id) ?? null;
          const prev = index > 0 ? visibleMessages[index - 1] : null;
          const prevDayKey = prev ? (dayKeyByMessageId.get(prev.id) ?? null) : null;
          const showDivider = prevDayKey === null || prevDayKey !== dayKey;

          return (
            <Fragment key={m.id}>
              {showDivider && <ChatDayDivider dayKey={dayKey} />}
              <div
                ref={(el) => {
                  if (el) {
                    messageElByIdRef.current.set(m.id, el);
                  } else {
                    messageElByIdRef.current.delete(m.id);
                  }
                }}
              >
                <ChatMessageRow
                  userId={userId}
                  message={m}
                  patientName={patientName}
                  highlighted={highlightedMessageId === m.id}
                />
              </div>
            </Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
